import React, { useState } from 'react'

import SanitizedHtml from '@/components/misc/SanitizedHtml'
import { sanitizeHtml, sanitizeText } from '@/lib/helpers/domHelpers'
import { cn } from '@/lib/utils'

interface Props {
  content: string
  collapseThreshold?: number // The number of characters after which the content will be collapsed
  allowHtml?: boolean // Whether to allow safe HTML tags & attributes when sanitizing the content
  noCollapse?: boolean
  expandLabel?: string
  showToggleLabels?: boolean
  className?: string
}

const CollapsibleContent: React.FC<Props> = ({
  content,
  collapseThreshold = 430,
  allowHtml,
  noCollapse = false,
  expandLabel = ' ...read more',
  showToggleLabels = true,
  className,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const toggleIsCollapsed = () => setIsCollapsed((prevState) => !prevState)

  const sanitizedContent = allowHtml
    ? sanitizeHtml(content)
    : sanitizeText(content)

  const wrapperClassName = cn(
    {
      'prose text-inherit! text-size-inherit font-inherit max-w-none':
        allowHtml,
    },
    className,
  )

  if (content.length < collapseThreshold || noCollapse) {
    return (
      <SanitizedHtml className={wrapperClassName} html={sanitizedContent} />
    )
  }

  const toggleClassNames = 'border-0 text-left pl-0 text-dim hover:underline'

  const readMoreLink = showToggleLabels
    ? `<span role="button" class='${toggleClassNames}' style="line-height: 1.4">${expandLabel}</span>`
    : `<span class='hover:underline'>...</span>`

  const collapsedContent =
    sanitizedContent.slice(0, collapseThreshold) + readMoreLink

  return (
    <>
      {isCollapsed ? (
        <div
          className={cn('cursor-pointer', wrapperClassName)}
          onClick={(event) => {
            event.stopPropagation()
            toggleIsCollapsed()
          }}
          title="Click to expand"
        >
          <SanitizedHtml html={collapsedContent} />
        </div>
      ) : (
        <div>
          <SanitizedHtml className={wrapperClassName} html={sanitizedContent} />
          {showToggleLabels && (
            <span
              role="button"
              className={toggleClassNames}
              onClick={(event) => {
                event.stopPropagation()
                toggleIsCollapsed()
              }}
            >
              Read less
            </span>
          )}
        </div>
      )}
    </>
  )
}

export default CollapsibleContent
